import {
  DEFAULT_LOCALE,
  LocaleCode,
  LocaleDefinition,
  SUPPORTED_LOCALES,
  resolveLocale
} from '@athwab/shared';
import { PropsWithChildren, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import i18n from '../../i18n/config';

type LocaleContextValue = {
  locale: LocaleCode;
  languageTag: string;
  direction: LocaleDefinition['direction'];
  availableLocales: readonly LocaleDefinition[];
  changeLanguage: (next: LocaleCode) => Promise<void>;
};

const LocaleContext = createContext<LocaleContextValue | undefined>(undefined);

const applyDocumentAttributes = (definition: LocaleDefinition) => {
  if (typeof document === 'undefined') {
    return;
  }

  const root = document.documentElement;
  const body = document.body;
  const { direction, languageTag, id } = definition;

  root.lang = languageTag;
  root.dir = direction;
  root.dataset.locale = id;
  root.classList.toggle('rtl', direction === 'rtl');
  root.classList.toggle('ltr', direction === 'ltr');

  if (body) {
    body.dir = direction;
    body.dataset.locale = id;
  }
};

export const LocaleProvider = ({ children }: PropsWithChildren) => {
  const [definition, setDefinition] = useState<LocaleDefinition>(() =>
    resolveLocale(i18n.language ?? DEFAULT_LOCALE)
  );

  useEffect(() => {
    const handleLanguageChanged = (language: string) => {
      setDefinition(resolveLocale(language ?? DEFAULT_LOCALE));
    };

    i18n.on('languageChanged', handleLanguageChanged);
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, []);

  useEffect(() => {
    applyDocumentAttributes(definition);
    // Update document title based on locale
    const brandName = i18n.t('layout.brand', { ns: 'common' });
    if (brandName && typeof document !== 'undefined') {
      document.title = brandName;
    }
  }, [definition]);

  const changeLanguage = useCallback(async (next: LocaleCode) => {
    await i18n.changeLanguage(next);
  }, []);

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale: definition.id,
      languageTag: definition.languageTag,
      direction: definition.direction,
      availableLocales: SUPPORTED_LOCALES,
      changeLanguage
    }),
    [changeLanguage, definition]
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};

export const useLocale = () => {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
};

